import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { SendHorizontal, Undo2, X } from 'lucide-react';
import { SearchType } from 'ws-messages';
import { IAppState } from '../redux/store/types';
import {
  setSearchTextAction,
  setSearchTypeAction,
  setIncludeArchivedAction,
  setViewModeAction,
  resetRecordsAction,
  prepareReadRecordsCommandAction,
} from '../redux/records/actions';
import { bindActionCreators, Dispatch } from 'redux';
import { ViewMode } from '../redux/records/types';

/**
 * A search component for filtering records.
 * Allows users to search through records using text input.
 */
class RecordSearchBase extends React.Component<IRecordSearchProps> {
  /**
   * Handles the search action
   */
  handleSearch = () => {
    const { showAllLinkable, searchText, searchType, includeArchived } = this.props;
    this.props.resetRecordsAction();

    this.props.prepareReadRecordsCommandAction({
      readAllLinkable: showAllLinkable,
      searchText,
      ...(searchType ? { searchType } : {}),
      ...(includeArchived ? { includeArchived } : {}),
    });
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Handles the cancel action
   */
  handleCancel = () => {
    const { showAllLinkable, searchText } = this.props;
    if (searchText) {
      this.props.setSearchTextAction('');
      this.props.resetRecordsAction();
      this.props.prepareReadRecordsCommandAction({ readAllLinkable: showAllLinkable });
    }
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Renders the search component.
   *
   * @returns The JSX element representing the search form
   */
  render() {
    const { t, searchText, searchType } = this.props;
    const useTextSearch = searchType === SearchType.Text;

    return (
      <div className={`table-row record input search ${(searchText && 'has-value') || ''}`}>
        <div className='required input-and-error-container'>
          {searchText && <label className='input-label'>{t('records.searchRecords')}</label>}
          <textarea
            rows={3}
            value={searchText}
            onChange={(e) => this.props.setSearchTextAction(e.target.value)}
            className='search-text'
            placeholder={!searchText ? t('records.searchRecords') : ''}
            autoFocus
          />
        </div>

        <div className='footer'>
          <div className='search-options'>
            <label title={t('records.tooltips.searchTypeText')}>
              <input type='checkbox' checked={useTextSearch} onChange={(e) => this.props.setSearchTypeAction(e.target.checked ? SearchType.Text : undefined)} />
              {t('records.searchTypeText')}
            </label>
            <label title={t('records.tooltips.includeArchived')}>
              <input type='checkbox' checked={this.props.includeArchived} onChange={(e) => this.props.setIncludeArchivedAction(e.target.checked)} />
              {t('records.includeArchived')}
            </label>
          </div>
          <div className='actions'>
            <button onClick={this.handleSearch} className='action-button search' title={t('records.search')}>
              <SendHorizontal />
            </button>
            <button onClick={this.handleCancel} className='action-button cancel' title={t('common.cancel')}>
              {searchText ? <X /> : <Undo2 />}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Props interface for RecordSearch component
 */
interface IRecordSearchProps {
  t: (key: string, options?: any) => string;
  searchText: string;
  searchType?: SearchType;
  includeArchived: boolean;
  setSearchTextAction: typeof setSearchTextAction;
  setSearchTypeAction: typeof setSearchTypeAction;
  setIncludeArchivedAction: typeof setIncludeArchivedAction;
  setViewModeAction: typeof setViewModeAction;
  showAllLinkable: boolean;
  resetRecordsAction: typeof resetRecordsAction;
  prepareReadRecordsCommandAction: typeof prepareReadRecordsCommandAction;
}

const mapStateToProps = (state: IAppState) => ({
  showAllLinkable: state.records.showAllLinkable,
  searchText: state.records.searchText,
  searchType: state.records.searchType,
  includeArchived: state.records.includeArchived,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setSearchTextAction,
      setSearchTypeAction,
      setIncludeArchivedAction,
      setViewModeAction,
      resetRecordsAction,
      prepareReadRecordsCommandAction,
    },
    dispatch,
  );

export const RecordSearch = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(RecordSearchBase));
